import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Building2, QrCode } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { assetsService } from "@/services/assets.service";
import { mediaUrl } from "@/lib/utils";
import { BrandInline } from "@/components/common/BrandLogo";

function Info({ label, value }) {
    return (
        <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {label}
            </div>
            <div className="text-sm text-foreground">{value ?? "—"}</div>
        </div>
    );
}

export default function PublicAssetPage() {
    const { id } = useParams();
    const query = useQuery({
        queryKey: ["public-asset", id],
        queryFn: () => assetsService.getPublic(id),
        retry: 0,
    });

    const a = query.data;

    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/40 py-10">
            <div className="mx-auto w-full max-w-3xl px-4 sm:px-6">
                <div className="mb-6 flex items-center justify-between">
                    <BrandInline subtitle="Ativo público" />
                    <Button asChild size="sm" variant="outline">
                        <Link to="/login" data-testid="public-asset-login-link">
                            Entrar
                        </Link>
                    </Button>
                </div>

                {query.isLoading ? (
                    <div className="space-y-4">
                        <Skeleton className="h-24 w-full" />
                        <Skeleton className="h-48 w-full" />
                    </div>
                ) : query.isError ? (
                    <Card className="rounded-xl border shadow-sm">
                        <CardContent className="p-8 text-center">
                            <div className="text-lg font-semibold">Ativo não encontrado</div>
                            <p className="mt-1 text-sm text-muted-foreground">
                                Verifique o link ou entre em contato com a empresa responsável.
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-6">
                        <Card className="rounded-xl border shadow-sm">
                            <CardHeader className="flex flex-row items-start gap-3 pb-2">
                                <div className="grid h-11 w-11 place-items-center rounded-lg bg-muted text-muted-foreground">
                                    <QrCode className="h-5 w-5" />
                                </div>
                                <div className="flex-1">
                                    <CardTitle className="text-lg font-semibold tracking-tight" data-testid="public-asset-title">
                                        {a.internal_code}
                                    </CardTitle>
                                    <div className="mt-1 flex flex-wrap items-center gap-2">
                                        {a.category && <Badge variant="outline">{a.category}</Badge>}
                                        {a.manufacturer && (
                                            <Badge variant="secondary" className="text-[10px]">
                                                {a.manufacturer}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="grid gap-4 md:grid-cols-3">
                                <Info label="Modelo" value={a.model} />
                                <Info label="Número de Série" value={a.serial_number} />
                                <Info label="Local" value={a.installation_location} />
                                <Info label="Tensão" value={a.voltage} />
                                <Info label="Potência" value={a.power} />
                                <Info label="RPM" value={a.rpm} />
                                <Info label="Frequência" value={a.frequency} />
                                <Info label="Ano" value={a.manufacturing_year} />
                            </CardContent>
                        </Card>

                        {(a.photos || []).length > 0 && (
                            <Card className="rounded-xl border shadow-sm">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold">Galeria</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="public-asset-gallery">
                                        {a.photos.map((p, i) => (
                                            <div key={p.id || p.url || `photo-${i}`} className="overflow-hidden rounded-xl border">
                                                <AspectRatio ratio={4 / 3}>
                                                    <img src={mediaUrl(p.url)} alt="" className="h-full w-full object-cover" />
                                                </AspectRatio>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {(a.company || a.customer) && (
                            <Card className="rounded-xl border shadow-sm">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-semibold">Referências</CardTitle>
                                </CardHeader>
                                <CardContent className="grid gap-4 md:grid-cols-2">
                                    {a.company && (
                                        <div className="flex items-center gap-3">
                                            <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground">
                                                <Building2 className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                                    Empresa Responsável
                                                </div>
                                                <div className="text-sm font-medium">{a.company.trade_name || a.company.legal_name}</div>
                                                <div className="text-xs text-muted-foreground">{a.company.engineering_license}</div>
                                            </div>
                                        </div>
                                    )}
                                    {a.customer && (
                                        <div className="flex items-center gap-3">
                                            <div className="grid h-9 w-9 place-items-center rounded-lg bg-muted text-muted-foreground">
                                                <Building2 className="h-4 w-4" />
                                            </div>
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                                    Cliente
                                                </div>
                                                <div className="text-sm font-medium">{a.customer.trade_name || a.customer.legal_name}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {[a.customer.city, a.customer.state].filter(Boolean).join("/")}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        <div className="pt-2 text-center text-[11px] text-muted-foreground">
                            © {new Date().getFullYear()} Prosper360 Engenharia Integrada · Página pública gerada automaticamente.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
